import { useEffect, useRef, useState } from "react";

export interface LegendLogicOptions {
  widthThreshold?: number;
  chartType: 'bar' | 'pie';
  displayType?: 'stacked' | 'grouped'; // For bar charts
  legendItems?: any[]; // To check if legend has 2 lines max
}

export interface LegendLogicResult {
  vizWrapperRef: React.RefObject<HTMLDivElement>;
  orientation: 'horizontal' | 'vertical';
  shouldShowLegend: boolean;
}

/**
 * 
 * Stacked Bar Chart:
 * - width < 400px: horizontal (max 25%)
 * - width >= 400px: vertical (100% height, unless bars < 70% height, then horizontal)
 * - max width 30% for vertical orientation
 * 
 * Pie Chart:
 * - width < 400px: horizontal (max 25%)
 * - width >= 400px: vertical (100% height, unless legend has max 2 lines, then bottom)
 * 
 * Bar Chart Legend:
 * - Only shows when displayType is 'stacked'
 */
export const useLegendOrientation = (options: LegendLogicOptions): LegendLogicResult => {
  const { 
    widthThreshold = 500, 
    chartType, 
    displayType,
    legendItems = []
  } = options;
  
  const vizWrapperRef = useRef<HTMLDivElement>(null);
  const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
  const [shouldShowLegend, setShouldShowLegend] = useState(false);

  useEffect(() => {
    const element = vizWrapperRef.current;
    if (!element) return;

    const updateLegendLogic = () => {
      const width = element.getBoundingClientRect().width;
      const height = element.getBoundingClientRect().height;
      const buffer = 50; // Buffer to prevent oscillation
      
      // Determine if legend should be shown
      let showLegend = true;
      if (chartType === 'bar') {
        // Bar chart legend only shows for stacked display type
        showLegend = displayType === 'stacked';
      }
      // Pie chart always shows legend
      
      setShouldShowLegend(showLegend);
      
      if (!showLegend) {
        setOrientation('horizontal'); // Default when not showing
        return;
      }

      // Determine orientation based on width and chart type
      let newOrientation: 'horizontal' | 'vertical' = 'horizontal';
      
      if (width < widthThreshold) {
        // Width < 400px: always horizontal
        newOrientation = 'horizontal';
      } else {
        // Width >= 400px: vertical by default
        newOrientation = 'vertical';
        
        // Special conditions to override to horizontal:
        if (chartType === 'bar' && displayType === 'stacked') {

        } else if (chartType === 'pie') {
          // For pie chart: if legend has max 2 lines, show it on bottom (horizontal)
          // Calculate characters per line based on container width
          const containerWidth = width * 0.8; // Account for padding (20% buffer)
          const charsPerLine = Math.floor(containerWidth / 8); // ~8px per character estimate
          
          const totalChars = legendItems.reduce((sum, item) => {
            if (Array.isArray(item.text)) {
              return sum + item.text.join(' ').length;
            }
            return sum + (item.text?.length || 0);
          }, 0);
          
          const estimatedLines = Math.ceil(totalChars / charsPerLine);
          
          if (estimatedLines <= 2) {
            newOrientation = 'horizontal';
          }
        }
      }
      
      // Apply hysteresis to prevent rapid switching
      setOrientation(prev => {
        if (prev === 'horizontal' && width >= widthThreshold + buffer && newOrientation === 'vertical') {
          return 'vertical';
        } else if (prev === 'vertical' && width < widthThreshold - buffer && newOrientation === 'horizontal') {
          return 'horizontal';
        }
        return newOrientation;
      });
    };

    // Set initial state
    updateLegendLogic();

    const resizeObserver = new ResizeObserver(updateLegendLogic);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, [widthThreshold, chartType, displayType, legendItems.length]);

  return {
    vizWrapperRef,
    orientation,
    shouldShowLegend
  };
};
